import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {} from '@adserve/adserve-react-components';
import { RadioButton, Spacer } from '@adserve/adserve-react-components';
import moment from 'moment';

import { sectionedPanelStyle } from '../../../../styles';
import ChartBase from '../../CheckAvails/Chart/Chart.Base';
import { ArgumentAxis, Label } from 'devextreme-react/cjs/chart';
import { SystemSettingsContext } from '../../../../contexts/SystemSettingsContext';
import ChartArea from '../../../../svg/ChartArea';
import ChartBar from '../../../../svg/ChartBar';

const PerformanceChart = ({
    campaign,
    performanceData,
    currentStation,
    dateRange,
    id = 'PerformanceChart',
    performanceStats = []
}) => {
    const { formatNumber, dateFormat } = useContext(SystemSettingsContext);
    const [chartType, setChartType] = useState('area');
    const [displayField, setDisplayField] = useState('Impacts');

    // TODO - remove after demo, since we just want to demonstrate the UI best to have a number generated for now
    const performanceStat1 = Math.floor(Math.random() * 658000);
    const performanceStat2 = Math.floor(Math.random() * 658000);
    const performanceStat3 = Math.floor(Math.random() * 658000);
    console.log('performancedata', performanceData);
    const titleStyle = {
        fontFamily: 'Roboto',
        fontSize: 18,
        color: '#000',
        textDecoration: 'none solid rgb(65, 65, 65)',
        fontWeight: 'bold'
    };

    const legendTextStyle = {
        fontFamily: 'Roboto',
        fontSize: 14,
        color: '#6FB882',
        textDecoration: 'none solid rgb(65, 65, 65)',
        fontWeight: 'bold'
    };

    const totalImpressionsStyle = {
        fontFamily: 'Roboto',
        fontSize: 14,
        color: '#414141',
        textDecoration: 'none solid rgb(65, 65, 65)',
        fontWeight: 'bold'
    };

    const chartOptionContainerStyle = {
        width: 63,
        height: 16,
        borderRadius: 5,
        border: '1px solid #E0E0E0',
        display: 'flex',
        padding: '6px 11px 8px 11px'
    };

    const radioTextStyle = {
        marginLeft: 5.5,
        marginRight: 8,
        fontSize: 15
    };

    const formattedData = (a1, a2) =>
        a1?.map((itm) => ({
            ...a2?.find((item) => item?.part === itm.part && item && ((item.Booked = item.value), true)),
            ...itm,
            Delivered: itm.value,
            week: `Week ${itm.part}`
        }));

    const { series, data } = useMemo(() => {
        const data = [];
        let series = [];

        if (performanceData?.length) {
            for (const spots of performanceData) {
                const item = data.find((d) => d.weekNum === spots.weekNum);
                const include = currentStation === '0' || spots.stationId === currentStation;

                if (include) {
                    if (item) {
                        item.orderedSpots += spots.orderedSpots;
                        item.deliveredSpots += spots.deliveredSpots;
                        item.targetImpacts += spots.targetImpacts;
                        item.targetPlayedImpacts += spots.targetPlayedImpacts;
                    } else {
                        data.push({
                            weekNum: spots.weekNum,
                            label: spots.label,
                            orderedSpots: spots.orderedSpots,
                            deliveredSpots: spots.deliveredSpots,
                            targetImpacts: spots.baseImpacts,
                            spotPrice: spots.spotPrice,
                            targetPlayedImpacts: spots.targetPlayedImpacts,
                            showingDays: spots.showingDays
                        });
                    }
                }
            }

            switch (displayField) {
                case 'Impacts':
                    series = [
                        {
                            valueField: 'targetImpacts',
                            name: 'Booked',
                            color: 'rgb(132, 211, 148)',
                            type: 'area'
                        },
                        {
                            valueField: 'targetPlayedImpacts',
                            name: 'Delivered',
                            color: '#b5c5eb',
                            type: 'bar',
                            barPadding: 0.3
                        }
                    ];
                    break;
                case 'Reach':
                    break;
                case 'Spots':
                    series = [
                        {
                            valueField: 'orderedSpots',
                            name: 'Ordered',
                            color: 'rgb(132, 211, 148)',
                            type: 'bar'
                        },
                        {
                            valueField: 'deliveredSpots',
                            name: 'Delivered',
                            color: '#b5c5eb',
                            type: 'bar'
                        }
                    ];
                    break;
                case 'OTH':
                    break;
                case 'Budget':
                    series = [
                        {
                            valueField: 'spotPrice',
                            name: 'Budget',
                            color: 'rgb(132, 211, 148)',
                            type: 'bar'
                        }
                    ];
                    break;
            }
        }
        return { series, data };
    }, [performanceData, displayField, currentStation]);

    const firstMonday = useMemo(
        () => (campaign?.startDate ? moment(campaign?.startDate).startOf('week') : null),
        [campaign?.startDate]
    );

    const visualRange = useMemo(() => {
        const weekNumStart = moment(dateRange[0]).diff(firstMonday, 'week');
        const weekNumEnd = moment(dateRange[1]).diff(firstMonday, 'week');
        return [weekNumStart, weekNumEnd];
    }, [dateRange, firstMonday]);

    return (
        <div
            id={id}
            style={{
                ...sectionedPanelStyle,
                padding: '18px 30px 10px 30px',
                height: 376,
                boxSizing: 'border-box',
                width: '100%'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    marginTop: 9,
                    alignItems: 'center'
                }}
            >
                <span style={titleStyle}>
                    {window.translate(displayField)} : {formatNumber(0)}
                </span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        <div onClick={() => setDisplayField('Impacts')} style={{ display: 'flex', cursor: 'pointer' }}>
                            <RadioButton width={16} height={16} value={displayField === 'Impacts'} />
                            <span style={radioTextStyle}> {window.translate('Impacts')} </span>
                        </div>

                        <div onClick={() => setDisplayField('Spots')} style={{ display: 'flex', cursor: 'pointer' }}>
                            <RadioButton width={16} height={16} value={displayField === 'Spots'} />
                            <span style={radioTextStyle}> {window.translate('Spots')} </span>
                        </div>

                        <div onClick={() => setDisplayField('Budget')} style={{ display: 'flex', cursor: 'pointer' }}>
                            <RadioButton width={16} height={16} value={displayField === 'Budget'} />
                            <span style={radioTextStyle}> {window.translate('Budget')} </span>
                        </div>
                    </div>

                    {/* <div style={chartOptionContainerStyle}>
                        <div style={{ cursor: 'pointer' }} onClick={() => setChartType('area')}>
                            <ChartArea height={19} width={25} fill={chartType === 'area' ? '#D7D7D7' : '#414141'} />
                        </div>
                        <Spacer width={11} height={0} />
                        <div style={{ cursor: 'pointer' }} onClick={() => setChartType('bar')}>
                            <ChartBar height={19} width={25} fill={chartType === 'bar' ? '#D7D7D7' : '#414141'} />
                            <div style={{ position: 'absolute', height: 8, width: 5, backgroundColor: chartType === 'bar' ? '#D7D7D7' : '#414141', marginTop: -16, marginLeft: 4 }} />
                            <div style={{ position: 'absolute', height: 16, width: 5, backgroundColor: chartType === 'bar' ? '#D7D7D7' : '#414141', marginTop: -24, marginLeft: 11 }} />
                            <div style={{ position: 'absolute', height: 13, width: 5, backgroundColor: chartType === 'bar' ? '#D7D7D7' : '#414141', marginTop: -21, marginLeft: 18 }} />
                        </div>
                    </div> */}
                </div>
            </div>

            <div style={{ marginTop: 9 }}>
                <ChartBase
                    series={series}
                    data={data}
                    argumentField='label'
                    legendSettings={{
                        verticalAlignment: 'bottom',
                        horizontalAlignment: 'left',
                        visible: true,
                        border: { color: '#FFB50C', width: 1, visible: false }
                    }}
                    height={260}
                >
                    <ArgumentAxis aggregationInterval={1} allowDecimals={false}>
                        <Label displayMode='standard' />
                    </ArgumentAxis>
                </ChartBase>
            </div>
        </div>
    );
};

export default PerformanceChart;
